// ProductMaster.jsx
import React, { useEffect, useState } from 'react';
import { FaEye, FaEdit, FaBan, FaDownload, FaPlus, FaSort, FaSortUp, FaSortDown, FaCheck, FaSearch, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import dayjs from 'dayjs';
import ProductFormModal from './ProductFormModal';
import ProductViewModal from './ProductViewModal';
import Sidebar from './Sidebar';
import { useScreenPermissions } from './useScreenPermissions';
import { useNavigate } from 'react-router-dom';

const LeftArrowIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/></svg>;
const RightArrowIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"/></svg>;

const API_URL = '/api';

const ProductMaster = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('All');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const navigate = useNavigate();

   // --- ADD THESE THREE NEW STATE VARIABLES ---
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSyncSuccess, setShowSyncSuccess] = useState(false);
  const [syncData, setSyncData] = useState(null);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // permissions
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  // THEME CSS VARIABLES (match Vendor/Customer)
  useEffect(() => {
    const themeVars = {
      "--sidebar-line-color": "#e6efee",
      "--sidebar-bg": "#ffffff",
      "--accent-color": "#2b8f85",
      "--accent-color1": "#b6d5d2ff",
      "--accent-hover": "#23756e",
      "--page-border-color": "#9ca3af",
      "--text-primary": "#334155",
      "--text-muted": "#475569",
      "--text-muted-2": "#64748b",
      "--disabled-color": "#bcd8d5",
      "--sort-inactive": "#cddfe0",
      "--search-icon": "#cbd5da",
      "--status-active-bg": "#e6f6f3",
      "--status-active-color": "#0f766e",
      "--status-inactive-bg": "#fafaf9",
      "--status-inactive-color": "#6b7280",
      "--page-bg": "#EEF6F5",
      "--table-header-bg": "#EEF6F5",
      "--surface-1": "#ffffff",
      "--surface-2": "#f6fbfa",
      "--divider": "#e6efee",
      "--danger": "#ef4444"
    };
    Object.entries(themeVars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));

    const styleId = "product-master-theme-overrides";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        .primary-btn {
          background-color: var(--accent-color);
          color: #ffffff;
          
        }
        .primary-btn:hover { background-color: var(--accent-hover); }

        .secondary-btn {
          background-color: #ffffff;
          color: var(--text-primary);
          border: 1px solid var(--page-border-color);
         
        }
        .secondary-btn:hover { background-color: #f8faf9; }

        

        .cust-status{
          border-radius: 9999px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          }

          .cust-status-in{
          border-radius: 6px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          }

        .accent-icon { color: var(--accent-color); }
        .accent-icon-hover:hover { color: var(--accent-hover); }

        .sort-active { color: var(--accent-color) !important; }
        .sort-inactive { color: var(--sort-inactive) !important; }

        .page-active { background-color: var(--accent-color); color: #fff;  }
        .page-inactive { color: var(--accent-color); }
        .page-inactive:hover { background-color: #f7fbfa; }

        .icon-btn { transition: color .15s ease; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, []);

 useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

 const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/products`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
    setLoading(false);
  };
 const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedProducts = [...products].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;

       if (key === 'CreatedAt') {
        return direction === 'asc' 
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }

            if (key === 'ProductCode') {
        return direction === 'asc'
          ? a[key].localeCompare(b[key], undefined, { numeric: true, sensitivity: 'base' })
          : b[key].localeCompare(a[key], undefined, { numeric: true, sensitivity: 'base' });
      }

       if (key === 'ProductTrackingType') {
              // The actual data property is 'AllowProductTracking' (values: 0, 1, 2)
              // We sort by the numeric value (0=None, 1=Batch, 2=Serial)
              const valA = a.AllowProductTracking ?? -1;
              const valB = b.AllowProductTracking ?? -1;
              return direction === 'asc' ? valA - valB : valB - valA;
            }

     // Handle string fields
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setProducts(sortedProducts);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ml-1" /> 
      : <FaSortDown className="ml-1" />;
  };


    const formatProductTracking = (trackingValue) => {
    if (trackingValue === undefined || trackingValue === null || trackingValue === '') return '-';
    
    const value = parseInt(trackingValue);
    switch(value) {
      case 0: return 'None';
      case 1: return 'Batch';
      case 2: return 'Serial';
      default: return '-';
    }
  };

  
  const handleView = (product) => {
    setSelectedProduct(product.ProductCode);
    setShowView(true);
  };

  const handleEdit = (product) => {
    setSelectedProduct(product.ProductCode);
    setIsEdit(true);
    setShowForm(true);
  };
 const handleDeactivate = async (product) => {
    if (!window.confirm('Are you sure you want to deactivate this product?')) return;
    
    try {
      const response = await fetch(`${API_URL}/products/${product.ProductCode}/deactivate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json','Authorization':`Bearer ${localStorage.getItem('authToken')}` },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to deactivate product');
      }
      
      fetchProducts();
    } catch (error) {
      console.error('Error deactivating product:', error);
      alert(error.message || 'An error occurred while deactivating the product');
    }
  };

   const handleReactivate = async (product) => {
    if (!window.confirm('Are you sure you want to reactivate this product?')) return;
    
    try {
      const response = await fetch(`${API_URL}/products/${product.ProductCode}/reactivate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization':`Bearer ${localStorage.getItem('authToken')}` },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reactivate product');
      }
      
      fetchProducts();
    } catch (error) {
      console.error('Error reactivating product:', error);
      alert(error.message || 'An error occurred while reactivating the product');
    }
  };

   const handleDownloadCSV = () => {
    const headers = [
      'ProductCode', 'ProductName', 'ProductTrackingType', 'CreatedAt'
    ];
    const rows = products.map(p => [p.ProductCode, p.ProductName, formatProductTracking(p.AllowProductTracking) || '-', dayjs(p.CreatedAt).format('YYYY-MM-DD HH:mm')]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'products.csv'; a.click();
    window.URL.revokeObjectURL(url);
  };

   const handleLogout = () => {
    navigate('/login');
  };

   const handleCreate = () => {
    setSelectedProduct(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    if (formLoading) return;
    setFormLoading(true);
    try {
      if (isEdit && selectedProduct) {
        const response = await fetch(`${API_URL}/products/${selectedProduct}`,{
  method: 'PUT',
  headers: {
    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
    // ❌ Do NOT set Content-Type when using FormData
  },
          body: formData,
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update product');
        }
      } else {
        const response = await fetch(`${API_URL}/products`, { method: 'POST',headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }, body: formData });
        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.message && errorData.message.includes('duplicate key')) {
            setShowForm(false);
            setTimeout(() => {
              setShowForm(true);
              alert('Duplicate product code detected. The form has been refreshed with a new product code. Please try again.');
            }, 100);
            return;
          }
          throw new Error(errorData.message || 'Failed to create product');
        }
      }
      setShowForm(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      alert(error.message || 'An error occurred while saving the product');
    } finally {
      setFormLoading(false);
    }
  };

   const filteredProducts = products.filter(product => {
    const lowerSearchTerm = searchTerm.toLowerCase();

    // Format the tracking type for search comparison (e.g., convert 1 to "batch")
    const trackingLabel = formatProductTracking(product.AllowProductTracking).toLowerCase();

    const matchesSearch = searchTerm === '' ||
      product.ProductCode.toLowerCase().includes(lowerSearchTerm) ||
      product.ProductName.toLowerCase().includes(lowerSearchTerm) ||
      (product.ItemCategory && product.ItemCategory.toLowerCase().includes(lowerSearchTerm)) ||
      // NEW: Search by the displayed Tracking Type text
      trackingLabel.includes(lowerSearchTerm);

    const matchesStatus = status === 'All' || product.Status === status;

    return matchesSearch && matchesStatus;
  });


   const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const productsForPage = filteredProducts.slice(startIndex, endIndex);


  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

    const handleSync = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    
    try {
      // Update this URL if your backend route is different. 
      // Based on your Vendor example, I used /mapping/bc-products/sync
      const res = await fetch(`${API_URL}/mapping/bc-products/sync`, { method: 'GET' ,headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      const data = await res.json();

      if (data.success) {
        setSyncData({
          total: data.recordCount,
          inserted: data.inserted,
          updated: data.updated
        });
        setShowSyncSuccess(true);
        fetchProducts(); // Refresh the list
      } else {
        alert(`Sync Failed: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Sync Error:", error);
      alert("An error occurred while syncing products.");
    } finally {
      setIsSyncing(false);
    }
  };

  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    pages.push(
      <button key="prev" className="p-1" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} style={{ color: 'var(--accent-color)', opacity: currentPage === 1 ? 0.45 : 1 }}>
        <LeftArrowIcon className="w-3 h-3" />
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button key={i} onClick={() => handlePageChange(i)} className={`px-4 py-1 transition-colors text-sm ${currentPage === i ? 'page-active' : 'page-inactive'}`}>
          {i}
        </button>
      );
    }

    pages.push(
      <button key="next" className="p-1" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} style={{ color: 'var(--accent-color)', opacity: (currentPage === totalPages || totalPages === 0) ? 0.45 : 1 }}>
        <RightArrowIcon className="w-3 h-3" />
      </button>
    );

    return pages;
  };

  const canCreate = hasFullAccess || hasPermission('PRODUCT_MASTER', 'CanCreate');
  const canView = hasFullAccess || hasPermission('PRODUCT_MASTER', 'CanView');
  const canEdit = hasFullAccess || hasPermission('PRODUCT_MASTER', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('PRODUCT_MASTER', 'CanDelete');

  if (permissionsLoading) {
    return (
      <div className="flex h-screen items-center justify-center" style={{ backgroundColor: 'var(--page-bg)' }}>
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-t-4 border-b-4" style={{ borderColor: 'var(--accent-color)' }}></div>
          <p className="mt-4 text-lg font-medium" style={{ color: 'var(--text-muted)' }}>Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Sidebar theme to match Create button color (active item / profile / logout)
  const sidebarTheme = {
    text: 'var(--text-primary, #334155)',
    muted: 'var(--text-muted, #475569)',
    border: 'var(--page-border-color, #e6efee)',
    sidebarBorder: 'var(--sidebar-line-color, #e6efee)',
    background: 'var(--sidebar-bg, #ffffff)',
    pageBackground: 'var(--page-bg, #EEF6F5)',
    activeBg: 'var(--accent-color, #2b8f85)',
    profileBg: 'var(--accent-color, #2b8f85)',
    activeText: '#ffffff',
    accent: 'var(--accent-color, #2b8f85)',
    accentHover: 'var(--accent-hover, #23756e)',
    hoverGray: 'rgba(0,0,0,0.04)',
    iconColor: 'var(--accent-color, #2b8f85)',
    sidebarBg: 'var(--sidebar-bg, #ffffff)',
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--page-bg)' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={sidebarTheme}
      onLogout={handleLogout} />
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
        {/* Header */}
        <div className="mb-8 ">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight leading-tight text-[color:var(--text-primary)] mb-1">Product Master</h1>
            </div>
            
            <div className="flex items-center gap-3">
               <button 
                onClick={handleSync} 
                disabled={isSyncing}
                className={`flex items-center gap-2 px-5 py-2 font-medium transition-all duration-200 shadow-sm border ${
                  isSyncing 
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                    : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'
                }`}
              >
                {isSyncing ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Syncing...
                  </>
                ) : (
                  <>
                    <FaCheck className="h-4 w-4" />
                    Sync 
                  </>
                )}
              </button>
              <button onClick={handleDownloadCSV} className="flex items-center gap-2 px-5 py-2 secondary-btn font-medium transition-all duration-200 shadow-sm">
                <FaDownload className="h-4 w-4" /> Export
              </button>
              {/* {canCreate && (
                <button onClick={handleCreate} className="flex items-center gap-2 px-5 py-2 primary-btn font-medium hover:shadow transition-all duration-200 shadow-sm rounded-none">
                  <FaPlus className="h-4 w-4" /> Create New
                </button>
              )} */}
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="p-5 bg-white border border-gray-300 shadow-md mb-6">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold " style={{ color: 'var(--text-muted)' }}>What are you looking for?</label>
              <div className="relative mt-2">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--search-icon)' }} />
                <input type="text" placeholder="Search for code, name, category..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pl-10 bg-white border border-gray-300" style={{ borderColor: 'var(--divider)', color: 'var(--text-primary)' }} />
              </div>
              {/* <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-48 py-2 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2c3e50] transition-all duration-200"
                >
                  <option>All</option>
                  <option>Retail</option>
                  <option>Wholesale</option>
                </select>
              </div> */}
            </div>
            {/* <div>
              <label className="block text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-4 py-2 border border-gray-300" style={{ borderColor: 'var(--divider)', background: 'var(--surface-1)', color: 'var(--text-primary)' }}>
                <option>All</option>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div> */}
            <button className="px-5 py-2 primary-btn font-medium rounded-none" onClick={() => setCurrentPage(1)}>Search</button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-hidden bg-[color:var(--surface-1)] border border-gray-300 shadow-md">
          <div className="flex justify-between items-center p-4 border-b border-gray-300" >
            <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Product Summary</h2>
            <div className="flex items-center space-x-2">
              {/* <select className="px-3 py-1 border rounded-md">
                  <option>Show All Columns</option>
                </select> */}
                {/* <button className="bg-[#3498db] text-white px-4 py-1 rounded-md text-sm font-semibold">
                  DISPATCH SELECTED
                </button> */}
              <div className="flex items-center space-x-2" style={{ color: 'var(--text-muted)' }}>
                {renderPaginationButtons()}
                <button title="Download CSV" className="p-2 rounded-full text-[color:var(--text-muted)] hover:bg-[color:var(--surface-2)] transition-all duration-200" onClick={handleDownloadCSV}>
                  <FaDownload />
                </button>
              </div>
            </div>
          </div>

          <div className="h-full overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ backgroundColor: 'var(--table-header-bg)' }} className="text-gray-600 text-xs font-semibold uppercase text-center">
                <tr>
                  <th className={`px-6 py-4 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort('ProductCode')}>
                    <div className="flex items-center">PRODUCT CODE {getSortIcon('ProductCode')}</div>
                  </th>
                  <th className={`px-6 py-4 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort('ProductName')}>
                    <div className="flex items-center">PRODUCT NAME {getSortIcon('ProductName')}</div>
                  </th>
                  <th className={`px-6 py-4 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort('ProductTrackingType')}>
                    <div className="flex items-center">PRODUCT TRACKING TYPE {getSortIcon('ProductTrackingType')}</div>
                  </th>
                   <th className={`px-6 py-4 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort('IsDismental')}>
                    <div className="flex items-center">IS DISMENTAL{getSortIcon('IsDismental')}</div>
                  </th>
                  {/* <th className={`px-6 py-4 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort('ItemCategory')}>
                    <div className="flex items-center">CATEGORY {getSortIcon('ItemCategory')}</div>
                  </th>
                  <th className={`px-6 py-4 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort('UOMName')}>
                    <div className="flex items-center">UOM {getSortIcon('UOMName')}</div>
                  </th>
                  <th className={`px-6 py-4 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort('Status')}>
                    <div className="flex items-center">STATUS {getSortIcon('Status')}</div>
                  </th>
                  <th className={`px-6 py-4 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort('CreatedAt')}>
                    <div className="flex items-center">CREATED AT {getSortIcon('CreatedAt')}</div>
                  </th> */}
                  <th className={`px-6 py-4 text-center uppercase ${isScrolled ? 'shadow' : ''}`}>ACTIONS</th>
                </tr>
              </thead>

              <tbody className="divide-y" style={{ borderTop: '1px solid var(--divider)' }}>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-[color:var(--text-muted-2)]">Loading products...</td>
                  </tr>
                ) : productsForPage.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-[color:var(--text-muted-2)]">No products found.</td>
                  </tr>
                ) : (
                  productsForPage.map((product, idx) => (
                    <tr key={product.ProductCode || idx} className="transition-all duration-300 hover:bg-[color:var(--surface-2)]">
                      <td className="px-6 py-4 text-left text-sm font-mono whitespace-nowrap">
                        <p onClick={() => canView && handleView(product)} className={`cursor-pointer transition-all duration-200 ${canView ? 'accent-icon-hover' : ''}`} style={{ color: 'var(--accent-color)', textDecoration: 'none' }}>
                          {(product.ProductCode || '').toUpperCase()}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-left text-sm whitespace-nowrap font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {(product.ProductName || '').toUpperCase()}
                      </td>
                      <td className="px-6 py-4 text-left text-sm whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                        {formatProductTracking(product.AllowProductTracking)}
                      </td>
                      <td className="px-6 py-4 text-left text-sm whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                        <span
    className={`px-2 py-1 rounded-full text-xs font-semibold ${
      product.IsDismental
        ? 'bg-green-100 text-green-700'
        : 'bg-red-100 text-red-700'
    }`}
  >
    {product.IsDismental ? 'True' : 'False'}
    </span>
                      </td>
                      {/* <td className="px-6 py-4 text-left text-sm whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                        {(product.ItemCategory || '-').toUpperCase()}
                      </td>
                      <td className="px-6 py-4 text-left text-sm whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                        {(product.UOMName || '-').toUpperCase()}
                      </td>
                      <td className="px-6 py-4 text-left text-sm ">
                        <span className= {`  rounded-full ${product.Status === "1" || product.Status === "Active"  ? 'cust-status bg-teal-50 text-teal-700 font-bold hover:bg-teal-100' : 'cust-status-in bg-amber-50 font-bold text-amber-700 hover:bg-amber-100 '}`}>
                          {product.Status === "1" || product.Status === "Active" ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-left text-sm whitespace-nowrap" style={{ color: 'var(--text-muted-2)' }}>
                        {product.CreatedAt ? (
                          <div className="flex flex-col leading-tight">
                            <span style={{ color: 'var(--text-primary)' }}>{dayjs(product.CreatedAt).format('DD MMM YYYY')}</span>
                            <span className="text-xs" style={{ color: 'var(--text-muted-2)' }}>{dayjs(product.CreatedAt).format('HH:mm')}</span>
                          </div>
                        ) : '-'}
                      </td> */}
                      
                      <td className="px-6 py-4 flex gap-2 justify-center items-center">
                        {canView && (
                          <button title="View" className="p-2 rounded-full icon-btn" onClick={() => handleView(product)} style={{ color: 'var(--accent-color)' }}>
                            <FaEye />
                          </button>
                        )}
                        {/* {canEdit && (
                          <button title="Edit" className="p-2 rounded-full icon-btn" onClick={() => handleEdit(product)} style={{ color: 'var(--accent-color)' }}>
                            <FaEdit />
                          </button>
                        )}
                        {canDelete && (
                          product.Status === 'Active' ? (
                            <button title="Deactivate" className="p-2 rounded-full icon-btn" onClick={() => handleDeactivate(product)} style={{ color: 'var(--danger)' }}>
                              <FaBan />
                            </button>
                          ) : (
                            <button title="Reactivate" className="p-2 rounded-full icon-btn" onClick={() => handleReactivate(product)} style={{ color: 'var(--accent-color)' }}>
                              <FaCheck />
                            </button>
                          )
                        )} */}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

          {showSyncSuccess && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 text-center transform transition-all scale-100">
                {/* Success Icon */}
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                  <FaCheck className="h-8 w-8 text-green-600" />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2">Sync Complete!</h3>
                <p className="text-gray-500 mb-6">The product data has been successfully synchronized.</p>


                <button
                  onClick={() => setShowSyncSuccess(false)}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white primary-btn shadow-sm hover:shadow focus:outline-none transition-all duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          )}

         {/* Modals */}
        <ProductFormModal
            open={showForm}
            onClose={() => setShowForm(false)}
            onSubmit={handleFormSubmit}
            initialData={selectedProduct}
            isEdit={isEdit}
            loading={formLoading}
          />
          <ProductViewModal
    open={showView}
    onClose={() => setShowView(false)}
    productCode={selectedProduct}
/>
      </main>
    </div>
  )
};

export default ProductMaster;
